import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BellRing, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../ui/Primitives';
import { ChampFiltre, ChampRecherche } from '../ui/Onglets';
import { BarreSites } from '../ui/BarreSites';
import { usePerimetre } from '../../site/usePerimetre';
import { useTaillePage } from '../../lib/useTaillePage';
import { ErreurApi } from '../../lib/api';
import {
  useEcheancesDuMois,
  type EcheanceProgrammee,
  type EcheancesDuMois as VueMois,
  type EtatEcheanceProgrammee,
  type TypeFacturation,
} from '../../lib/useFinancements';

const MOIS_VALIDE = /^\d{4}-(?:0[1-9]|1[0-2])$/;

const TONS_TYPE: Record<TypeFacturation, 'navy' | 'blue' | 'mint'> = {
  APPRENTISSAGE: 'navy',
  PROFESSIONNALISATION: 'blue',
  FRANCE_TRAVAIL: 'mint',
};

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Comparaison sans casse ni accent, pour la recherche. */
function cle(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function correspond(ligne: EcheanceProgrammee, recherche: string): boolean {
  const cherche = cle(recherche.trim());
  if (!cherche) return true;
  const { dossier } = ligne;
  return [
    `${dossier.apprenant.prenom} ${dossier.apprenant.nom}`,
    `${dossier.apprenant.nom} ${dossier.apprenant.prenom}`,
    dossier.employeur ?? '',
    dossier.formation,
    dossier.payeur ?? '',
  ].some((valeur) => cle(valeur).includes(cherche));
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/**
 * Échéances du mois (TF-465) : toutes les échéances programmées du mois, les
 * trois types de facturation confondus — fin d'échéance et 1er équipement d'un
 * contrat d'apprentissage, mois d'un contrat de professionnalisation, fin d'un
 * parcours France Travail —, avec l'état et le montant que le bloc Financement
 * montre. On passe d'un mois à l'autre ; `?mois=AAAA-MM` garde celui qu'on lit.
 *
 * Tout vient de `/api/financements/echeances` (`domain/echeances-du-mois.ts`) :
 * l'écran filtre et pagine ce qu'elle rend, il ne compte rien.
 */
export function EcheancesDuMois({ onVoirAFacturer }: { onVoirAFacturer: () => void }): JSX.Element {
  const { site } = usePerimetre();
  const [parametres, setParametres] = useSearchParams();
  const demande = parametres.get('mois');
  const mois = demande && MOIS_VALIDE.test(demande) ? demande : null;
  const { data, isLoading, isFetching, error } = useEcheancesDuMois(site, mois);
  const [type, setType] = useState<TypeFacturation | ''>('');
  const [etat, setEtat] = useState<EtatEcheanceProgrammee | ''>('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => setPage(1), [site, mois, type, etat, recherche, taillePage]);

  const allerA = (cible: string | null): void =>
    setParametres(
      (avant) => {
        const suivants = new URLSearchParams(avant);
        suivants.set('onglet', 'echeances');
        if (cible) suivants.set('mois', cible);
        else suivants.delete('mois');
        return suivants;
      },
      { replace: true },
    );

  const lignes = useMemo(
    () =>
      (data?.lignes ?? []).filter(
        (ligne) =>
          (!type || ligne.typeFacturation === type) && (!etat || ligne.etat === etat) && correspond(ligne, recherche),
      ),
    [data?.lignes, type, etat, recherche],
  );
  const total = lignes.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const affichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return lignes.slice(debut, debut + taillePage);
  }, [lignes, page, taillePage]);

  return (
    <>
      <BarreSites />

      <NavigationMois vue={data ?? null} chargement={isFetching} onAllerA={allerA} />

      {error && (
        <MessageErreur
          message={error instanceof ErreurApi ? error.message : 'Les échéances du mois n’ont pas pu être chargées.'}
        />
      )}

      {data && <CompteursDuMois vue={data} />}

      {data && data.anterieures > 0 && (
        <div className="flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <BellRing className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-amber-900">
              {pluriel(data.anterieures, 'échéance')} des mois précédents{' '}
              {data.anterieures > 1 ? 'restent' : 'reste'} à facturer.
            </div>
            <p className="text-xs text-amber-800">
              Le module d’alertes les signale au pôle facturation ; aucune facture n’est créée d’elle-même.
            </p>
          </div>
          <BoutonSecondaire onClick={onVoirAFacturer}>Voir ce qui est à facturer</BoutonSecondaire>
        </div>
      )}

      <Carte
        titre={data ? `Échéances · ${data.mois.libelle}` : 'Échéances du mois'}
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher une échéance"
              placeholder="Apprenant, employeur, payeur"
            />
            <ChampFiltre
              valeur={type}
              onChange={(valeur) => setType(valeur as TypeFacturation | '')}
              etiquette="Filtrer par type de facturation"
            >
              <option value="">Toutes les facturations</option>
              {data?.compteurs.parType.map((option) => (
                <option key={option.type} value={option.type}>
                  {option.libelle}
                </option>
              ))}
            </ChampFiltre>
            <ChampFiltre
              valeur={etat}
              onChange={(valeur) => setEtat(valeur as EtatEcheanceProgrammee | '')}
              etiquette="Filtrer par état"
            >
              <option value="">Tous les états</option>
              {data?.compteurs.parEtat.map((option) => (
                <option key={option.etat} value={option.etat}>
                  {option.libelle} ({option.nombre})
                </option>
              ))}
            </ChampFiltre>
          </div>
        }
        sansBordure
      >
        {isLoading && <Chargement libelle="Chargement des échéances…" />}

        {!isLoading && !error && total === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            {data && data.lignes.length === 0
              ? 'Aucune échéance programmée ce mois-ci dans ce périmètre.'
              : 'Aucune échéance ne correspond aux filtres actifs.'}
          </p>
        )}

        {/* Sous la tablette, une carte par échéance : le tableau y défilerait de côté. */}
        {!isLoading && !error && total > 0 && (
          <ul className="divide-y divide-gray-100 md:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.cle} className="space-y-2 px-4 py-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                      {jourFrancais(ligne.date)}
                    </div>
                    <LienDossier ligne={ligne} />
                    <div className="text-xs text-codialis-gray">{ligne.dossier.formation}</div>
                  </div>
                  <Pastille texte={ligne.etatLibelle} ton={ligne.ton} />
                </div>
                <Echeance ligne={ligne} />
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span className="min-w-0 break-words text-codialis-gray">
                    {ligne.dossier.payeur ?? 'Payeur non renseigné'}
                  </span>
                  <Montant ligne={ligne} />
                </div>
                {ligne.detail && <p className="text-[11px] text-codialis-gray">{ligne.detail}</p>}
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !error && total > 0 && (
          <div className="relative hidden overflow-x-auto md:block">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Date', 'Apprenant', 'Échéance', 'Payeur', 'Montant', 'État'].map((colonne) => (
                    <th
                      key={colonne}
                      className={`px-3 py-3 text-[11px] font-bold uppercase tracking-wider text-codialis-gray lg:px-4 ${
                        colonne === 'Montant' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {colonne}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.cle} className="align-top transition hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-3 font-semibold tabular-nums text-codialis-navy lg:px-4">
                      {jourFrancais(ligne.date)}
                    </td>
                    <td className="px-3 py-3 lg:px-4">
                      <LienDossier ligne={ligne} />
                      <div className="text-xs text-codialis-gray">{ligne.dossier.formation}</div>
                      <div className="mt-1.5">
                        <PastilleSite site={ligne.dossier.siteCode} />
                      </div>
                    </td>
                    <td className="px-3 py-3 lg:px-4">
                      <Echeance ligne={ligne} />
                    </td>
                    <td className="px-3 py-3 lg:px-4">
                      <div className="break-words text-sm text-codialis-navy">
                        {ligne.dossier.payeur ?? <span className="text-amber-700">Non renseigné</span>}
                      </div>
                      {ligne.dossier.employeur && (
                        <div className="break-words text-[11px] text-codialis-gray">{ligne.dossier.employeur}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right lg:px-4">
                      <Montant ligne={ligne} />
                    </td>
                    <td className="max-w-[240px] space-y-1 px-3 py-3 lg:px-4">
                      <Pastille texte={ligne.etatLibelle} ton={ligne.ton} />
                      {ligne.detail && <p className="text-[11px] text-codialis-gray">{ligne.detail}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="échéance"
          />
        )}
      </Carte>
    </>
  );
}

function NavigationMois({
  vue,
  chargement,
  onAllerA,
}: {
  vue: VueMois | null;
  chargement: boolean;
  onAllerA: (mois: string | null) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Mois affiché">
      <button
        type="button"
        onClick={() => vue && onAllerA(vue.mois.precedent)}
        disabled={!vue}
        aria-label="Mois précédent"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </button>
      <p
        className="font-display flex min-w-[11rem] items-center justify-center gap-2 text-lg font-bold text-codialis-navy"
        aria-live="polite"
        aria-busy={chargement}
      >
        <CalendarDays className="h-5 w-5 shrink-0 text-codialis-sky" aria-hidden />
        {vue?.mois.libelle ?? '…'}
      </p>
      <button
        type="button"
        onClick={() => vue && onAllerA(vue.mois.suivant)}
        disabled={!vue}
        aria-label="Mois suivant"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50"
      >
        <ChevronRight className="h-5 w-5" aria-hidden />
      </button>
      {vue && !vue.mois.courant && (
        <BoutonSecondaire onClick={() => onAllerA(null)}>Revenir au mois en cours</BoutonSecondaire>
      )}
    </div>
  );
}

function CompteursDuMois({ vue }: { vue: VueMois }): JSX.Element {
  const { total, parType } = vue.compteurs;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Compteur
        libelle="Échéances du mois"
        valeur={total.nombre}
        detail={[
          total.montant ? `${total.montant} programmés` : null,
          total.sansMontant > 0 ? `${total.sansMontant} sans montant connu` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        ton="warm"
      />
      {parType.map((compte) => (
        <Compteur
          key={compte.type}
          libelle={compte.libelle}
          valeur={compte.nombre}
          detail={compte.montant ?? (compte.nombre > 0 ? 'montant à venir' : 'aucune échéance')}
          ton={TONS_TYPE[compte.type]}
        />
      ))}
    </div>
  );
}

function LienDossier({ ligne }: { ligne: EcheanceProgrammee }): JSX.Element {
  const { apprenant } = ligne.dossier;
  return (
    <Link
      to={`/adm-dossier/${apprenant.id}#financement`}
      className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
    >
      {apprenant.prenom} {apprenant.nom}
    </Link>
  );
}

function Echeance({ ligne }: { ligne: EcheanceProgrammee }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <div className="break-words text-sm font-medium text-codialis-navy">{ligne.libelle}</div>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-codialis-gray">
        <span>{ligne.periode}</span>
        <Pastille texte={ligne.typeLibelle} ton="info" />
      </div>
    </div>
  );
}

function Montant({ ligne }: { ligne: EcheanceProgrammee }): JSX.Element {
  if (!ligne.montant) return <span className="text-codialis-gray">—</span>;
  return (
    <span className="inline-block text-right">
      <span className="block font-semibold tabular-nums text-codialis-navy">{ligne.montant.texte}</span>
      <span className="block text-[11px] text-codialis-gray">{ligne.montant.libelle}</span>
    </span>
  );
}
